/**
 * Route Metadata Helper
 * Provides utility functions to extract menu structure from router routes.
 */

import type { RouteRecordRaw } from 'vue-router'
import { log } from '../utils/DebugLogger'
import { config } from '../services/configService'

export interface RouteMeta {
  showInMenu?: boolean
  /**
   * Overrides the feature key of this route. Without it the key is the route
   * name, lowercased — which forces one route per feature. Two pages of the
   * same feature (Site: unidades + leads) need distinct route names but must
   * share one key, so they declare it here.
   */
  feature?: string
  permissions?: string[]
  /** Capacidade exigida (skill app-groups): substitui `permissions` onde a regra não é um nome de grupo. */
  capability?: string
  /** Groups that never see this item in the menu (menu only — the route keeps its own access rule). */
  hideFor?: string[]
  menuParent?: string | null
  menuOrder?: number
  title?: string
  icon?: string
  isParent?: boolean
  [key: string]: unknown
}

export interface MenuRoute {
  name?: string
  path: string
  meta: RouteMeta
}

export interface UserPermissions {
  user?: {
    groups?: string[]
  }
  /** Capacidades do app-api (CapacidadesDoUsuario, skill app-groups) — lidas por `meta.capability`. */
  capacidades?: string[]
}

export interface MenuItem {
  id: string | undefined
  label: string | undefined
  icon: string | null
  route: string | null
  command: (() => void) | null
  children: MenuItem[]
}

type AnyRoute = MenuRoute | RouteRecordRaw

function getMeta(route: AnyRoute): RouteMeta {
  return (route.meta || {}) as RouteMeta
}

/**
 * The tenant-feature key this route is gated by: `meta.feature` when declared,
 * otherwise the route name. `null` means "not gateable" (an unnamed route).
 * The single place the convention lives — the sidebar filter and the router's
 * beforeEach both read it, so a URL can never be reachable while its menu item
 * is hidden.
 */
export function featureKeyOf(route: { name?: unknown; meta?: unknown }): string | null {
  const meta = (route.meta || {}) as RouteMeta
  const declared = meta.feature
  if (typeof declared === 'string' && declared.trim() !== '') return declared
  return typeof route.name === 'string' ? route.name : null
}

export function getRoutesForMenu<T extends AnyRoute>(routes: T[], userPermissions: UserPermissions | null = null): T[] {
  log('RouteMetadata', 'getRoutesForMenu', 'Extracting menu routes', { routesCount: routes.length, userPermissions })

  let menuRoutes = routes.filter((route) => {
    const meta = getMeta(route)
    return meta.showInMenu === true
  })

  const allMenuRoutes = menuRoutes
  if (userPermissions) {
    menuRoutes = menuRoutes.filter((route) => hasAccess(route, userPermissions))
  }

  const beforeFeatureFilterCount = menuRoutes.length
  menuRoutes = menuRoutes.filter((route) => {
    const key = featureKeyOf(route)
    return key === null || config.isFeatureEnabled(key)
  })
  const removedByFeatureFilter = beforeFeatureFilterCount - menuRoutes.length
  if (removedByFeatureFilter > 0) {
    log('RouteMetadata', 'getRoutesForMenu', 'Filtered routes by disabled features', {
      removedCount: removedByFeatureFilter
    })
  }

  menuRoutes = withParentsOfCapabilityRoutes(menuRoutes, allMenuRoutes)
  const groups = userPermissions?.user?.groups ?? []
  // hideFor esconde o item de quem só chegaria a ele por um grupo escondido. Quem TAMBÉM tem um
  // grupo que a própria rota libera continua vendo: uma conta Full que acumula Franqueado ou
  // Gerente (comum na base real) perdia o menu Configurações inteiro por causa do segundo grupo.
  menuRoutes = menuRoutes.filter((route) => {
    const meta = getMeta(route)
    const escondidos = meta.hideFor ?? []
    if (!escondidos.some((g) => groups.includes(g))) return true
    return (meta.permissions ?? []).some((g) => groups.includes(g) && !escondidos.includes(g))
  })

  log('RouteMetadata', 'getRoutesForMenu', `Found ${menuRoutes.length} menu routes`)
  return menuRoutes
}

/**
 * A route gated by a capability (skill app-groups) is decided by the app-api, not by a group list —
 * so a parent's `permissions` can't hide it: whoever holds the capability (e.g. a delegated admin of
 * an app) would reach the page but never see the menu path to it. Its ancestors are shown too.
 * Only capability routes pull parents in; a parent never shows otherwise.
 */
function withParentsOfCapabilityRoutes<T extends AnyRoute>(visible: T[], all: T[]): T[] {
  const byName = new Map(all.map((r) => [r.name, r]))
  const result = [...visible]
  for (const route of visible) {
    if (!getMeta(route).capability) continue
    let parent = byName.get(getMeta(route).menuParent ?? undefined)
    while (parent) {
      if (!result.includes(parent)) result.push(parent)
      parent = byName.get(getMeta(parent).menuParent ?? undefined)
    }
  }
  return result
}

export function hasAccess(route: AnyRoute, userPermissions: UserPermissions): boolean {
  const meta = getMeta(route)
  if (meta.capability) {
    return hasCapability(meta.capability, userPermissions?.capacidades)
  }
  if (!meta.permissions) {
    return true
  }

  const userGroups = userPermissions?.user?.groups

  if (!userGroups || !Array.isArray(userGroups)) {
    return meta.permissions.includes('Public')
  }

  return userGroups.some((userGroup) => meta.permissions!.includes(userGroup))
}

/**
 * A pessoa tem a capacidade? As capacidades vêm do app-api (/permissoes/pegaPermissoes →
 * CapacidadesDoUsuario, skill app-groups). O menu (hasAccess) e o beforeEach leem daqui.
 */
export function hasCapability(capability: string, capacidades: unknown): boolean {
  return Array.isArray(capacidades) && capacidades.includes(capability)
}

export function buildMenuTree<T extends AnyRoute>(routes: T[]): MenuItem[] {
  log('RouteMetadata', 'buildMenuTree', `Building menu tree from ${routes.length} routes`)

  const sortedRoutes = [...routes].sort((a, b) => {
    const orderA = getMeta(a).menuOrder || 0
    const orderB = getMeta(b).menuOrder || 0
    return orderA - orderB
  })

  const topLevelRoutes = sortedRoutes.filter((route) => {
    const parent = getMeta(route).menuParent
    return !parent || parent === null
  })

  const menuTree = topLevelRoutes.map((route) => buildMenuItem(route, sortedRoutes))

  log('RouteMetadata', 'buildMenuTree', `Built menu tree with ${menuTree.length} top-level items`)
  return menuTree
}

function buildMenuItem<T extends AnyRoute>(route: T, allRoutes: T[]): MenuItem {
  const meta = getMeta(route)
  const menuItem: MenuItem = {
    id: route.name as string | undefined,
    label: meta.title,
    icon: meta.icon ? `pi ${meta.icon}` : null,
    route: meta.isParent ? null : (route.path as string),
    command: meta.isParent ? null : () => {},
    children: []
  }

  const children = allRoutes.filter((r) => getMeta(r).menuParent === route.name)

  children.sort((a, b) => {
    const orderA = getMeta(a).menuOrder || 0
    const orderB = getMeta(b).menuOrder || 0
    return orderA - orderB
  })

  if (children.length > 0) {
    menuItem.children = children.map((child) => buildMenuItem(child, allRoutes))
  }

  return menuItem
}

export function getRouteByName<T extends AnyRoute>(routes: T[], name: string): T | null {
  return routes.find((route) => route.name === name) || null
}

export function getRouteByPath<T extends AnyRoute>(routes: T[], path: string): T | null {
  return routes.find((route) => route.path === path) || null
}

export function getChildRoutes<T extends AnyRoute>(routes: T[], parentName: string): T[] {
  return routes.filter((route) => getMeta(route).menuParent === parentName)
}
